import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { GET_SHUTTLE_SERVICE_DETAILS } from 'graphql/shuttleService/queries'

export default function ShuttleServiceDetails() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [shuttleInfo, setShuttleInfo] = useState(null)
    const [countries, setCountries] = useState([])

    const [getShuttleServiceDetails, { loading: shuttleLoading, error: shuttleError }] = useLazyQuery(
        GET_SHUTTLE_SERVICE_DETAILS,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchShuttleServiceProvider.success && data.searchShuttleServiceProvider.code === '200') {
                    setShuttleInfo(data.searchShuttleServiceProvider.providers[0])
                }
            },
        }
    )

    useEffect(() => {
        getShuttleServiceDetails({
            variables: {
                searchShuttleServiceProviderId: id,
                page: {
                    limit: 1,
                    offset: 0,
                },
            },
        })
    }, [id])

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.country) {
                setCountries(data.country)
            }
        },
    })

    useEffect(() => {
        getCountryList({
            variables: { code: null },
        })
    }, [])

    const getCountryFlag = (countryCode) => {
        return `https://flagcdn.com/w20/${countryCode?.toLowerCase()}.png`
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <div className="flex">
                    <button
                        onClick={() => {
                            navigate('/system-config/shuttle-services')
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-semibold leading-7 text-primary ps-2 pb-1"> {t('Shuttle Service Details')}</h3>
                </div>
                <button
                    onClick={() => navigate(`/system-config/shuttle-services/edit/${shuttleInfo?.provider_id}`)}
                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    {t('Edit Shuttle Service')}
                </button>
            </div>
            {shuttleLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {shuttleError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch shuttle service details!')}</div>
                </div>
            )}

            {!shuttleLoading && !shuttleError && shuttleInfo && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        {/* Provider Information */}
                        <div className="mt-5">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary"> {t('Provider Information')}</h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Provider Name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.provider_name}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Service Description (English)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.service_description.en}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Service Description (Arabic)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.service_description.ar}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Status')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                shuttleInfo.isactive 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {shuttleInfo.isactive ? t('Active') : t('Inactive')}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Contact Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Country')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.country_code && (
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={getCountryFlag(shuttleInfo.country_code)}
                                                        alt={shuttleInfo.country_code}
                                                        className="w-5 h-3"
                                                        onError={e => {
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                    {countries
                                                        .filter(country => country.alpha_iso2 === shuttleInfo.country_code)
                                                        .map(country => (
                                                            <span key={country.alpha_iso2}>
                                                                {country.country} ({country.country_ar})
                                                            </span>
                                                        ))}
                                                </div>
                                            )}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Contact Person')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.contact.person}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Mobile Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.contact.mobile_number}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Email')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.contact.email}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Address')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.contact.address}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Pricing Information */}
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Pricing Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Base Price')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.price.base} {shuttleInfo.price.currency}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Tax Amount')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.price.tax} {shuttleInfo.price.currency}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Gross Amount')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <span className="font-semibold text-primary">
                                                {shuttleInfo.price.gross} {shuttleInfo.price.currency}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* User Audit Information */}
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Audit Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Created By')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.user_audit.created_by || t('N/A')}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Created Date')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.user_audit.created_date 
                                                ? new Date(shuttleInfo.user_audit.created_date).toLocaleString()
                                                : t('N/A')
                                            }
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Modified By')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.user_audit.modified_by || t('N/A')}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Modified Date')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {shuttleInfo.user_audit.modified_date 
                                                ? new Date(shuttleInfo.user_audit.modified_date).toLocaleString()
                                                : t('N/A')
                                            }
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
